import React, { useEffect, useState } from 'react'
import { Box, Button, Text } from 'grommet'
import { I18n } from 'galarm-config'
import GalarmWebHeader from './GalarmWebHeader'
import { getAppPremiumProducts } from '../utils/getAppPremiumProducts'
import { PlanCards, FreeTrialBanner, FeatureList } from './PremiumComponents'

const StepsToBuyPremium = () => (
  <Box
    direction="row"
    align="start"
    justify="center"
    gap="none"
    margin={{ bottom: 'small' }}>
    {[
      { num: '1', label: I18n.t('verifyAccount') },
      { num: '2', label: I18n.t('choosePlan') },
      { num: '3', label: I18n.t('checkout') }
    ].map((step, i) => (
      <React.Fragment key={step.num}>
        <Box align="center" gap="xsmall" width="xsmall">
          <Box
            width="36px"
            height="36px"
            round="full"
            background="brand"
            align="center"
            justify="center"
            flex={false}>
            <Text color="white" weight="bold" size="small">
              {step.num}
            </Text>
          </Box>
          <Text size="xsmall" weight="bold" color="dark-2" textAlign="center">
            {step.label}
          </Text>
        </Box>
        {i < 2 && (
          <Box
            alignSelf="start"
            style={{
              height: '2px',
              width: '48px',
              background: '#d0d0d8',
              marginTop: '17px',
              flexShrink: 0
            }}
          />
        )}
      </React.Fragment>
    ))}
  </Box>
)

const PremiumLandingScreen = ({ onGetStarted }) => {
  const [product, setProduct] = useState(null)
  const [loadingProducts, setLoadingProducts] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    getAppPremiumProducts()
      .then(result => {
        const firstProduct = (result.products || [])[0] || null
        if (!firstProduct || !Array.isArray(firstProduct.prices)) {
          setError(I18n.t('unableToLoadPremiumPlans'))
          setLoadingProducts(false)
          return
        }
        setProduct(firstProduct)
        setLoadingProducts(false)
      })
      .catch(() => {
        setError(I18n.t('unableToLoadPremiumPlans'))
        setLoadingProducts(false)
      })
  }, [])

  const monthlyPrice = product?.prices?.find(
    p => p.recurring?.interval === 'month'
  )
  const yearlyPrice = product?.prices?.find(
    p => p.recurring?.interval === 'year'
  )
  const lifetimePrice = product?.prices?.find(p => p.type === 'one_time')

  return (
    <Box fill background="#f9f9fb">
      <GalarmWebHeader />

      {error && (
        <Box
          pad={{ horizontal: 'large', vertical: 'xsmall' }}
          background="status-error">
          <Text color="white" size="small" textAlign="center">
            {error}
          </Text>
        </Box>
      )}

      <Box flex overflow="auto">
        <Box align="center" pad="large" flex={{ shrink: 0 }}>
          <Box width="100%" gap="large" style={{ maxWidth: '960px' }}>
            {/* Hero */}
            <Box align="center">
              <Text
                size="xxlarge"
                weight="bold"
                color="dark-1"
                style={{ letterSpacing: '-0.5px', marginTop: '8px' }}>
                {I18n.t('galarmPremium')}
              </Text>
            </Box>

            {/* Plan cards */}
            {!loadingProducts && (
              <Box gap="small">
                <PlanCards
                  monthlyPrice={monthlyPrice}
                  yearlyPrice={yearlyPrice}
                  lifetimePrice={lifetimePrice}
                  readOnly
                />
                <FreeTrialBanner />
              </Box>
            )}

            {loadingProducts && (
              <Box align="center">
                <Text color="dark-4" size="small">
                  {I18n.t('loadingProducts')}
                </Text>
              </Box>
            )}

            <Box style={{ marginTop: '-12px', marginBottom: '-24px' }}>
              <StepsToBuyPremium />
            </Box>

            {/* CTA */}
            <Button
              primary
              size="large"
              alignSelf="center"
              label={<Text color="white">{I18n.t('getStarted')}</Text>}
              onClick={onGetStarted}
              disabled={loadingProducts}
              style={{ borderRadius: '8px', fontWeight: 600 }}
            />

            {/* Features */}
            <FeatureList />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default PremiumLandingScreen
